import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { Group, Text } from '@mantine/core'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useMutationSendBookingEmail } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID, type CoreBookingDocument } from '@flecto/types'
import {
  Modal,
  ModalsContext,
  useNotifications,
  useModal,
  Badge,
} from '@flecto/ui'

import { type BookingActionOption } from '../../../booking-actions/hooks/useBookingActions'
import {
  BookingSendEmail,
  type BookingSendEmailRef,
} from '../../../booking-details/components/BookingSendEmail/BookingSendEmail'
import { useBookingStore } from '../../../booking-details/hooks/useBookingStore'
import { ModalViewSingleDocument } from '../../../booking-documents/components/ModalViewSingleDocument/ModalViewSingleDocument'

export type ModalBookingSendEmailProps = {
  template?: BookingActionOption
  document?: CoreBookingDocument
  onSuccess?: () => void
}

export function ModalBookingSendEmail({
  template,
  document,
  onSuccess,
}: Readonly<ModalBookingSendEmailProps>) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_actions' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t: tBooking } = useTranslation('saas', { keyPrefix: 'booking' })

  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([])
  const bookingSendEmailRef = useRef<BookingSendEmailRef>(null)
  const { mutateAsync: sendBookingEmail, isLoading } =
    useMutationSendBookingEmail()
  const { showNotification } = useNotifications()
  const { closeModal } = useContext(ModalsContext)
  const { booking } = useBookingStore()
  const track = useAnalytics('segment')
  const { t: tBookingCustomerLng } = useTranslation('saas', {
    keyPrefix: 'booking',
    lng: booking?.customer?.locale ?? 'en',
  })

  const { openModal: openViewSingleDocModal } = useModal(
    <ModalViewSingleDocument
      id={booking?.id as ParameterID}
      documentId={document?.id as ParameterID}
    />
  )

  const getEmailBookingLink = ({
    bookLink,
    label,
  }: {
    bookLink: string
    label: string
  }): string => {
    return `<a href="${bookLink}" style=" background-color: #147D63; border: 1px solid #147D63; border-color: #147D63; border-radius: 6px; border-width: 1px; color: #ffffff; max-width: 220px; display: inline-block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; font-weight: normal; letter-spacing: 0px; line-height: normal; padding: 16px 24px 16px 24px; text-align: center; text-decoration: none; border-style: solid;" target="_blank" rel="noreferrer">${label}</a>`
  }

  const host = process.env['FLECTO_LINK_URL'] ?? 'https://flec.to'
  const permalink = booking?.permalink ?? ''
  const book_link = `${host}/${permalink}?tab=documents`

  const handleSubmit = async () => {
    if (!bookingSendEmailRef.current) {
      return
    }

    const { isValid, form } = bookingSendEmailRef.current.getFormState()

    if (!isValid || !booking?.customer?.email) {
      bookingSendEmailRef.current.validateForm()
      return
    }

    try {
      let body = form.message
      const unsignedDocumentsQtd = form.hasUnsignedDocuments(
        form.documents || []
      ).length
      if (unsignedDocumentsQtd) {
        body = `${body}<br /><br /><br />${getEmailBookingLink({
          bookLink: book_link,
          label:
            unsignedDocumentsQtd > 1
              ? tBookingCustomerLng('send_email.sign_documents')
              : tBookingCustomerLng('send_email.sign_document'),
        })}`
      }
      const payload = {
        internal_id: booking?.internal_id,
        body: body,
        attachment_document_ids: form.documents,
        cc: '',
        ...form,
      }
      await sendBookingEmail(payload)
      closeModal()

      if (document) {
        openViewSingleDocModal()
      }

      showNotification({
        type: 'success',
        message: t('notification_action_send_email_success'),
      })
      onSuccess?.()
    } catch (error) {
      console.error(error)
      showNotification({
        type: 'error',
        message: t('notification_action_send_email_error'),
      })
    }
  }

  useEffect(() => {
    track('SEND_EMAIL_BTN', {
      template: template?.value as
        | 'custom'
        | 'quote'
        | 'contract'
        | 'proforma'
        | 'invoice',
    })
  }, [])

  const getSelectedDocumentsLength = useMemo(() => {
    return selectedDocuments.length
  }, [selectedDocuments])

  return (
    <Modal size='xl'>
      <Modal.Close />
      <Modal.Icon icon={faEnvelope} color='gray.7' withCircleBackground />
      <Modal.Title>{t('modal_action_send_email_title')}</Modal.Title>
      <Modal.Content>
        <BookingSendEmail
          template={template}
          ref={bookingSendEmailRef}
          document={document}
          bookLink={book_link}
          onDocumentsChange={setSelectedDocuments}
          onInitialDocuments={setSelectedDocuments}
        />
      </Modal.Content>
      {Boolean(getSelectedDocumentsLength) && (
        <Modal.FooterExtra>
          <Group justify='flex-end'>
            <Badge color='teal' variant='light' ml='auto'>
              <Text fw={500}>
                {getSelectedDocumentsLength} {tBooking('send_email.attachment')}
              </Text>
            </Badge>
          </Group>
        </Modal.FooterExtra>
      )}
      <Modal.Cancel onClick={document ? openViewSingleDocModal : undefined} />
      <Modal.Submit
        color='brand'
        disabled={isLoading}
        preventClose
        onClick={handleSubmit}
      >
        {tActions('send_email')}
      </Modal.Submit>
    </Modal>
  )
}
